'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { 
  Shield,
  User,
  Download,
  Trash2,
  Edit,
  Lock,
  Unlock,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Calendar,
  ChevronDown,
  Eye,
  Users,
  Database,
  Globe,
  Settings,
  Mail
} from 'lucide-react'
import type { 
  BirhausPrivacyManagerProps,
  PrivacyRequest,
  DataSubjectRight,
  PrivacyRequestStatus
} from '../types/compliance'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Data subject rights configuration
const rightsConfig: Record<DataSubjectRight, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
  descriptionEs: string
  descriptionEn: string
}> = {
  acceso: {
    icon: <Eye size={20} />,
    colorClass: 'text-blue-600 bg-blue-100',
    nameEs: 'Derecho de Acceso',
    nameEn: 'Right to Access',
    descriptionEs: 'Acceder a datos personales procesados',
    descriptionEn: 'Access personal data being processed'
  },
  rectificacion: {
    icon: <Edit size={20} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Derecho de Rectificación',
    nameEn: 'Right to Rectification',
    descriptionEs: 'Corregir datos personales inexactos',
    descriptionEn: 'Correct inaccurate personal data'
  },
  eliminacion: {
    icon: <Trash2 size={20} />,
    colorClass: 'text-red-600 bg-red-100',
    nameEs: 'Derecho al Olvido',
    nameEn: 'Right to Erasure',
    descriptionEs: 'Solicitar eliminación de datos personales',
    descriptionEn: 'Request deletion of personal data'
  },
  portabilidad: {
    icon: <Download size={20} />,
    colorClass: 'text-purple-600 bg-purple-100',
    nameEs: 'Derecho de Portabilidad',
    nameEn: 'Right to Portability',
    descriptionEs: 'Recibir datos en formato estructurado',
    descriptionEn: 'Receive data in structured format'
  },
  limitacion: {
    icon: <Lock size={20} />,
    colorClass: 'text-orange-600 bg-orange-100',
    nameEs: 'Derecho de Limitación',
    nameEn: 'Right to Restriction',
    descriptionEs: 'Limitar el procesamiento de datos',
    descriptionEn: 'Restrict data processing'
  },
  oposicion: {
    icon: <XCircle size={20} />,
    colorClass: 'text-yellow-600 bg-yellow-100',
    nameEs: 'Derecho de Oposición',
    nameEn: 'Right to Object',
    descriptionEs: 'Oponerse al procesamiento de datos',
    descriptionEn: 'Object to data processing'
  },
  no_automatizacion: {
    icon: <Settings size={20} />,
    colorClass: 'text-indigo-600 bg-indigo-100',
    nameEs: 'No Decisiones Automatizadas',
    nameEn: 'No Automated Decisions',
    descriptionEs: 'No estar sujeto a decisiones automatizadas',
    descriptionEn: 'Not be subject to automated decisions'
  }
}

// Request status configuration
const statusConfig: Record<PrivacyRequestStatus, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  recibida: {
    icon: <Clock size={16} />,
    colorClass: 'text-blue-700 bg-blue-100',
    nameEs: 'Recibida',
    nameEn: 'Received'
  },
  en_proceso: {
    icon: <Settings size={16} />,
    colorClass: 'text-yellow-700 bg-yellow-100',
    nameEs: 'En Proceso',
    nameEn: 'In Progress'
  },
  completada: {
    icon: <CheckCircle size={16} />,
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'Completada',
    nameEn: 'Completed'
  },
  rechazada: {
    icon: <XCircle size={16} />,
    colorClass: 'text-red-700 bg-red-100',
    nameEs: 'Rechazada',
    nameEn: 'Rejected'
  },
  parcialmente_completada: {
    icon: <AlertCircle size={16} />,
    colorClass: 'text-orange-700 bg-orange-100',
    nameEs: 'Parcialmente Completada',
    nameEn: 'Partially Completed'
  }
}

/**
 * BirhausPrivacyManager - Data privacy request management component
 * 
 * Features:
 * - Comprehensive GDPR/LGPD compliance for data subject rights
 * - Automated deadline tracking (30 days response requirement)
 * - Multi-language support (Spanish-first for local regulations)
 * - Request type categorization with visual indicators
 * - Identity verification tracking and documentation
 * - System impact analysis and affected data tracking
 * - Response template management and history
 * - Miller's Law compliance (max 7 visible requests per view)
 * - Integration with audit trail for compliance documentation
 */
export function BirhausPrivacyManager({
  requests = [],
  onRequestClick,
  onCreateRequest,
  onUpdateStatus,
  showCreateButton = true,
  showStatusActions = true,
  className,
  ...props
}: BirhausPrivacyManagerProps) {
  const { language, reportViolation } = useBirhaus()
  const [searchTerm, setSearchTerm] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState<PrivacyRequestStatus | 'all'>('all')
  const [filterType, setFilterType] = React.useState<DataSubjectRight | 'all'>('all')
  const [showOverdueOnly, setShowOverdueOnly] = React.useState(false)

  // BIRHAUS validation: Check cognitive load
  React.useEffect(() => {
    if (requests.length > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Privacy manager shows ${requests.length} requests (max recommended: 7)`,
        messageEs: `Gestor de privacidad muestra ${requests.length} solicitudes (máximo recomendado: 7)`,
        messageEn: `Privacy manager shows ${requests.length} requests (max recommended: 7)`,
        recommendation: 'Use pagination or filtering to reduce cognitive load',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [requests.length, reportViolation])

  // Check if request is overdue (GDPR: 30 days, can extend to 90 days in complex cases)
  const isOverdue = React.useCallback((request: PrivacyRequest) => {
    return new Date() > request.fechaLimite && !['completada', 'rechazada'].includes(request.estado)
  }, [])

  // Check if request is approaching deadline (within 7 days)
  const isApproachingDeadline = React.useCallback((request: PrivacyRequest) => {
    const daysUntilDeadline = Math.floor(
      (request.fechaLimite.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    )
    return daysUntilDeadline <= 7 && daysUntilDeadline >= 0 && !['completada', 'rechazada'].includes(request.estado)
  }, [])

  // Filter requests
  const filteredRequests = React.useMemo(() => {
    let filtered = requests

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(request =>
        request.solicitante.nombres.toLowerCase().includes(term) ||
        request.solicitante.apellidos.toLowerCase().includes(term) ||
        request.solicitante.email.toLowerCase().includes(term) ||
        request.descripcion.toLowerCase().includes(term) ||
        request.datosAfectados.some(data => data.toLowerCase().includes(term))
      )
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(request => request.estado === filterStatus)
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(request => request.tipo === filterType)
    }

    // Overdue filter
    if (showOverdueOnly) {
      filtered = filtered.filter(request => isOverdue(request))
    }

    return filtered.sort((a, b) => {
      // Prioritize overdue requests
      const aOverdue = isOverdue(a)
      const bOverdue = isOverdue(b)
      if (aOverdue && !bOverdue) return -1
      if (!aOverdue && bOverdue) return 1
      
      // Then by deadline approaching
      const aApproaching = isApproachingDeadline(a)
      const bApproaching = isApproachingDeadline(b)
      if (aApproaching && !bApproaching) return -1
      if (!aApproaching && bApproaching) return 1
      
      // Finally by request date
      return new Date(b.fechaSolicitud).getTime() - new Date(a.fechaSolicitud).getTime()
    })
  }, [requests, searchTerm, filterStatus, filterType, showOverdueOnly, isOverdue, isApproachingDeadline])

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    const total = filteredRequests.length
    const pending = filteredRequests.filter(r => ['recibida', 'en_proceso'].includes(r.estado)).length
    const overdue = filteredRequests.filter(r => isOverdue(r)).length
    const approaching = filteredRequests.filter(r => isApproachingDeadline(r)).length
    const completed = filteredRequests.filter(r => r.estado === 'completada').length
    
    // Average response time for completed requests
    const completedWithTime = filteredRequests.filter(r => r.estado === 'completada' && r.fechaCompletado)
    const avgResponseTime = completedWithTime.length > 0
      ? Math.round(
          completedWithTime.reduce((sum, r) => {
            const days = Math.floor(
              (r.fechaCompletado!.getTime() - r.fechaSolicitud.getTime()) / (1000 * 60 * 60 * 24)
            )
            return sum + days
          }, 0) / completedWithTime.length
        )
      : 0

    return {
      total,
      pending,
      overdue,
      approaching,
      completed,
      avgResponseTime
    }
  }, [filteredRequests, isOverdue, isApproachingDeadline])

  // Handle actions
  const handleRequestClick = React.useCallback((request: PrivacyRequest) => {
    onRequestClick?.(request)
  }, [onRequestClick])

  const handleCreateRequest = React.useCallback(() => {
    onCreateRequest?.()
  }, [onCreateRequest])

  const handleUpdateStatus = React.useCallback((requestId: string, status: PrivacyRequestStatus) => {
    onUpdateStatus?.(requestId, status)
  }, [onUpdateStatus])

  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-birhaus-foreground">
            {language === 'es' ? 'Gestor de Privacidad' : 'Privacy Manager'}
          </h1>
          <p className="text-birhaus-muted-foreground">
            {language === 'es' 
              ? 'Gestión de solicitudes de derechos de datos personales'
              : 'Data subject rights request management'
            }
          </p>
        </div>
        
        {showCreateButton && onCreateRequest && (
          <button
            onClick={handleCreateRequest}
            className="flex items-center gap-2 px-4 py-2 bg-birhaus-primary text-white rounded-lg hover:bg-birhaus-primary/90 transition-colors"
          >
            <Plus size={16} />
            <span>{language === 'es' ? 'Nueva Solicitud' : 'New Request'}</span>
          </button>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-birhaus-foreground">{summaryStats.total}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Total' : 'Total'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-yellow-600">{summaryStats.pending}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Pendientes' : 'Pending'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-red-600">{summaryStats.overdue}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Vencidas' : 'Overdue'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-orange-600">{summaryStats.approaching}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Próx. Venc.' : 'Due Soon'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-green-600">{summaryStats.completed}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Completadas' : 'Completed'}
          </p>
        </div>
        
        <div className="bg-white p-3 border border-birhaus-border rounded-lg text-center">
          <p className="text-2xl font-bold text-blue-600">{summaryStats.avgResponseTime}</p>
          <p className="text-xs text-birhaus-muted-foreground">
            {language === 'es' ? 'Días Prom.' : 'Avg Days'}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-birhaus-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={language === 'es' ? 'Buscar solicitudes...' : 'Search requests...'}
            className="w-full pl-10 pr-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:border-birhaus-primary"
          />
        </div>

        {/* Status Filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as PrivacyRequestStatus | 'all')}
          className="px-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
        >
          <option value="all">{language === 'es' ? 'Todos los Estados' : 'All Statuses'}</option>
          {Object.entries(statusConfig).map(([key, config]) => (
            <option key={key} value={key}>
              {language === 'es' ? config.nameEs : config.nameEn}
            </option>
          ))}
        </select>

        {/* Type Filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as DataSubjectRight | 'all')}
          className="px-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
        >
          <option value="all">{language === 'es' ? 'Todos los Derechos' : 'All Rights'}</option>
          {Object.entries(rightsConfig).map(([key, config]) => (
            <option key={key} value={key}>
              {language === 'es' ? config.nameEs : config.nameEn}
            </option>
          ))}
        </select>

        {/* Overdue Only Toggle */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showOverdueOnly}
            onChange={(e) => setShowOverdueOnly(e.target.checked)}
            className="rounded border-birhaus-border"
          />
          <span>{language === 'es' ? 'Solo vencidas' : 'Overdue only'}</span>
        </label>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12 bg-white border border-birhaus-border rounded-lg">
            <Shield size={48} className="mx-auto mb-4 text-birhaus-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-birhaus-foreground mb-2">
              {language === 'es' ? 'No hay solicitudes de privacidad' : 'No privacy requests found'}
            </h3>
            <p className="text-birhaus-muted-foreground mb-4">
              {searchTerm || filterStatus !== 'all' || filterType !== 'all' || showOverdueOnly ? (
                language === 'es' 
                  ? 'No se encontraron solicitudes con los filtros aplicados'
                  : 'No requests found with applied filters'
              ) : (
                language === 'es'
                  ? 'Las solicitudes de derechos de datos aparecerán aquí'
                  : 'Data subject rights requests will appear here'
              )}
            </p>
            {showCreateButton && onCreateRequest && (
              <button
                onClick={handleCreateRequest}
                className="inline-flex items-center gap-2 px-4 py-2 bg-birhaus-primary text-white rounded-lg hover:bg-birhaus-primary/90 transition-colors"
              >
                <Plus size={16} />
                <span>{language === 'es' ? 'Crear Solicitud' : 'Create Request'}</span>
              </button>
            )}
          </div>
        ) : (
          filteredRequests.map((request) => {
            const rightConfig = rightsConfig[request.tipo]
            const statusInfo = statusConfig[request.estado]
            const overdue = isOverdue(request)
            const approachingDeadline = isApproachingDeadline(request)
            const isClickable = Boolean(onRequestClick)

            const daysUntilDeadline = Math.floor(
              (request.fechaLimite.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            )

            return (
              <div
                key={request.id}
                className={cn(
                  'bg-white p-6 border rounded-lg transition-all duration-200',
                  overdue ? 'border-red-300 bg-red-50' : 
                  approachingDeadline ? 'border-orange-300 bg-orange-50' : 'border-birhaus-border',
                  isClickable && 'cursor-pointer hover:shadow-md hover:border-birhaus-primary/50'
                )}
                onClick={() => isClickable && handleRequestClick(request)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={cn('p-3 rounded-full', rightConfig.colorClass)}>
                      {rightConfig.icon}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-birhaus-foreground">
                          {request.solicitante.nombres} {request.solicitante.apellidos}
                        </h3>
                        <span className={cn(
                          'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full',
                          statusInfo.colorClass
                        )}>
                          {statusInfo.icon}
                          <span>{language === 'es' ? statusInfo.nameEs : statusInfo.nameEn}</span>
                        </span>
                        
                        {overdue && (
                          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full text-red-700 bg-red-100">
                            <AlertTriangle size={12} />
                            <span>{language === 'es' ? 'Vencida' : 'Overdue'}</span>
                          </span>
                        )}
                        
                        {approachingDeadline && !overdue && (
                          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full text-orange-700 bg-orange-100">
                            <Clock size={12} />
                            <span>
                              {daysUntilDeadline} {language === 'es' ? 'días' : 'days'}
                            </span>
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-birhaus-muted-foreground mb-3">
                        {language === 'es' ? rightConfig.nameEs : rightConfig.nameEn}
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Contacto' : 'Contact'}
                          </p>
                          <p className="font-medium flex items-center gap-1">
                            <Mail size={12} />
                            {request.solicitante.email}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Fecha Solicitud' : 'Request Date'}
                          </p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar size={12} />
                            {request.fechaSolicitud.toLocaleDateString(language === 'es' ? 'es-PY' : 'en-US')}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Fecha Límite' : 'Deadline'}
                          </p>
                          <p className={cn(
                            'font-medium flex items-center gap-1',
                            overdue ? 'text-red-600' : approachingDeadline ? 'text-orange-600' : ''
                          )}>
                            <Clock size={12} />
                            {request.fechaLimite.toLocaleDateString(language === 'es' ? 'es-PY' : 'en-US')}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Responsable' : 'Assignee'}
                          </p>
                          <p className="font-medium flex items-center gap-1">
                            <User size={12} />
                            {request.responsable}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status action buttons */}
                  {showStatusActions && onUpdateStatus && (
                    <div className="flex items-center gap-2">
                      {request.estado === 'recibida' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUpdateStatus(request.id, 'en_proceso')
                          }}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                        >
                          <Settings size={12} />
                          <span>{language === 'es' ? 'Procesar' : 'Process'}</span>
                        </button>
                      )}
                      
                      {request.estado === 'en_proceso' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUpdateStatus(request.id, 'completada')
                            }}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          >
                            <CheckCircle size={12} />
                            <span>{language === 'es' ? 'Completar' : 'Complete'}</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleUpdateStatus(request.id, 'rechazada')
                            }}
                            className="flex items-center gap-1 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            <XCircle size={12} />
                            <span>{language === 'es' ? 'Rechazar' : 'Reject'}</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Description */}
                <p className="text-sm text-birhaus-foreground mb-4 line-clamp-2">
                  {request.descripcion}
                </p>
                
                {/* Affected data and systems */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  {request.datosAfectados.length > 0 && (
                    <div>
                      <p className="text-xs text-birhaus-muted-foreground mb-2 flex items-center gap-1">
                        <Database size={12} />
                        {language === 'es' ? 'Datos Afectados:' : 'Affected Data:'}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {request.datosAfectados.slice(0, 5).map((data, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                          >
                            {data}
                          </span>
                        ))}
                        {request.datosAfectados.length > 5 && (
                          <span className="px-2 py-1 text-xs bg-birhaus-muted text-birhaus-muted-foreground rounded">
                            +{request.datosAfectados.length - 5}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {request.sistemasAfectados.length > 0 && (
                    <div>
                      <p className="text-xs text-birhaus-muted-foreground mb-2 flex items-center gap-1">
                        <Globe size={12} />
                        {language === 'es' ? 'Sistemas Afectados:' : 'Affected Systems:'}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {request.sistemasAfectados.slice(0, 3).map((system, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded"
                          >
                            {system}
                          </span>
                        ))}
                        {request.sistemasAfectados.length > 3 && (
                          <span className="px-2 py-1 text-xs bg-birhaus-muted text-birhaus-muted-foreground rounded">
                            +{request.sistemasAfectados.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Identity verification */}
                <div className="flex items-center gap-4 text-xs text-birhaus-muted-foreground">
                  <span className="flex items-center gap-1">
                    {request.verificacionIdentidad ? (
                      <>
                        <CheckCircle size={12} className="text-green-600" />
                        <span>{language === 'es' ? 'Identidad verificada' : 'Identity verified'}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={12} className="text-yellow-600" />
                        <span>{language === 'es' ? 'Verificación pendiente' : 'Verification pending'}</span>
                      </>
                    )}
                  </span>
                  
                  {request.procesadoPor && (
                    <span>
                      {language === 'es' ? 'Procesado por:' : 'Processed by:'} {request.procesadoPor}
                    </span>
                  )}
                  
                  {request.fechaCompletado && (
                    <span>
                      {language === 'es' ? 'Completado:' : 'Completed:'} {request.fechaCompletado.toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export type { BirhausPrivacyManagerProps }